import * as angular from 'angular';
import * as _ from 'lodash';

import { Identifier, IdentifierValues, KeyValuePair, TagDateValue } from '../../models';
import { DependencyDto, ImportResultDto, GroupDto } from '../../models/DTO';
import { IdentifierType } from '../../models/enum';
import { Helper } from '../../common/helpers/helper';
import { IRequestService } from '../request.service';
import { IUploadService, IGroupService } from '../../services';
import { IDateRangeService } from '../general';

export interface IidentifierService {
    canDelete(guidList: string[]): angular.IPromise<KeyValuePair<string, boolean>[]>;
    getRules(guid: string): angular.IPromise<any[]>;
    getIdentifierValues(guid: string): angular.IPromise<IdentifierValues[]>;
    getIdentifierValues(guid: string, beginDate: any, endDate: any): angular.IPromise<IdentifierValues[]>;
    updateRules(guid, rules: any[]): angular.IPromise<number>;
    getPrecedents(guid: string): angular.IPromise<any>;
    getDependency(guid: string): angular.IPromise<DependencyDto[]>;
    getDependencyAverage(dependencyList: DependencyDto[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<TagDateValue[]>;
    getDependencyStatuses(dependencyList: DependencyDto[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<any[]>;
    getNativeIdentifierByGuid(guid: string): angular.IPromise<Identifier>;
    getNativeIdentifier(identifier: Identifier): angular.IPromise<Identifier>;
    getNativeIdentifiers(identifiers: Identifier[]): angular.IPromise<KeyValuePair<Identifier, Identifier>[]>;
    upload(file: File, groupGuid: string): angular.IPromise<ImportResultDto<Identifier>>;
}

export class IdentifierService implements IidentifierService {
    static serviceId = 'identifierService';
    static $inject = [
        '$q',
        'requestService',
        'dateRangeService',
        'groupService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService,
        private _groupService: IGroupService,
        private _upload: IUploadService
    ) {
    }

    private controllerUrl() {
        return `${this._requestService.getUrlV1()}identifier/`;
    }

    canDelete(guidList: string[]): angular.IPromise<KeyValuePair<string, boolean>[]> {
        if (guidList && guidList.length > 0) {
            const url = `${this.controllerUrl()}canDelete`;
            const data = guidList;
            // not cached elsewhere never able to delete
            //return this._requestService.cachedPostAtV1('identifier/canDelete', data);
            return this._requestService.post(url, data);
        } else {
            const defered = this._$q.defer<any>();
            defered.resolve(guidList);
            return defered.promise;
        }
    }

    getRules(guid: string): angular.IPromise<any[]> {
        const url = `${this.controllerUrl()}rules/${guid}`;
        return this._requestService.get(url);
    }

    getIdentifierValues(guid: string): angular.IPromise<IdentifierValues[]>;
    getIdentifierValues(guid: string, beginDate: any, endDate: any): angular.IPromise<IdentifierValues[]>;
    getIdentifierValues(guid: string, beginDate?: any, endDate?: any): angular.IPromise<IdentifierValues[]> {
        if (!beginDate && !endDate) {

            beginDate = this._dateRangeService.beginDate() as string;
            endDate = this._dateRangeService.endDate() as string;
        }
        const url = `${this.controllerUrl()}values`;
        const params = {
            begindate: beginDate, //this._dateRangeService.beginDate() as string,
            endDate: endDate, //this._dateRangeService.endDate() as string,
            guid: guid
        };
        return this._requestService.get(url, params);
    }

    updateRules(guid, rules: any[]): angular.IPromise<number> {
        const defered = this._$q.defer<number>();
        const url = `${this.controllerUrl()}updateRules`;
        const data = {
            identifierGuid: guid,
            rules: rules
        };
        this._requestService
            .post(url, data)
            .then(result => {
                this._requestService.cleanCache(`${this.controllerUrl()}rules/${guid}`);
                this._groupService.cleanGroupCache();
                defered.resolve(result);
            })
            .catch(reason => defered.reject(reason));

        return defered.promise;
    }

    getPrecedents(guid: string): angular.IPromise<any> {
        return this._$q.when([
            {
                guid: Helper.getGuid(),
                'name': 'Michel',
                type: 'temperature',
                unit: 'C'
            }, {
                guid: Helper.getGuid(),
                'name': 'Paul',
                type: 'temperature',
                unit: 'C'
            }, {
                guid: Helper.getGuid(),
                'name': 'Pierre',
                type: 'temperature',
                unit: 'C'
            }
        ]);
    }

    getDependency(identifierGuid: string): angular.IPromise<DependencyDto[]> {
        const url = `${this.controllerUrl()}dependency/${identifierGuid}`;
        // no cache since we cannot clean cache properly when updating occurrence matrix
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    getDependencyAverage(dependencyList: DependencyDto[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<TagDateValue[]> {
        const params = {
            beginDate: beginDate,
            endDate: endDate
        };
        const controllerAction = 'identifier/dependency/average';
        if (force) {
            this._requestService.cleanCachedPostValue(controllerAction, dependencyList, params);
        }
        return this._requestService.cachedPostAtV1(controllerAction, dependencyList, params);
        // const url = `${this.controllerUrl()}dependency/average`;
        // return this._requestService.post(url, dependencyList, params);
    }

    getDependencyStatuses(dependencyList: DependencyDto[], beginDate: string, endDate: string, force?: boolean): angular.IPromise<any[]> {
        const controllerAction = 'identifier/dependency/statuses';
        const params = {
            beginDate: beginDate,
            endDate: endDate
        };
        if (force) {
            this._requestService.cleanCachedPostValue(controllerAction, dependencyList, params);
        }
        return this._requestService.cachedPostAtV1(controllerAction, dependencyList, params);
        // const url = `${this.controllerUrl()}dependency/statuses`;
        // return this._requestService.post(url, dependencyList, params);
    }

    getNativeIdentifier(identifier: Identifier): angular.IPromise<Identifier> {
        const defered = this._$q.defer<Identifier>();

        if (identifier.type === IdentifierType.Linked) {
            defered.resolve(this.getNativeIdentifierByGuid(identifier.linkedGuid));
        } else {
            defered.resolve(identifier);
        }

        return defered.promise;
    }

    getNativeIdentifierByGuid(guid: string): angular.IPromise<Identifier> {
        const defered = this._$q.defer<Identifier>();

        this._groupService.getIdentifier(guid).then(identifier => {
            defered.resolve(this.getNativeIdentifier(identifier));
        }).catch(reason => {
            defered.reject(reason);
        });

        return defered.promise;
    }

    getNativeIdentifiers(identifiers: Identifier[]): angular.IPromise<KeyValuePair<Identifier, Identifier>[]> {
        const defered = this._$q.defer<KeyValuePair<Identifier, Identifier>[]>();

        if (identifiers.length > 0) {
            const alreadyNative = identifiers
                .filter(i => i.type !== IdentifierType.Linked)
                .map(i => <KeyValuePair<Identifier, Identifier>>{ key: i, value: i });

            const linkedList = identifiers.filter(i => i.type === IdentifierType.Linked);

            if (linkedList.length > 0) {
                this._groupService.getIdentifiers(linkedList.map(i => i.linkedGuid))
                    .then(nativeIdentifiers => defered.resolve(
                        linkedList.map(i => <KeyValuePair<Identifier, Identifier>>{ key: i, value: _.find(nativeIdentifiers, n => n.guid === i.guid) })
                    )).catch(reason => defered.reject(reason));
            } else {
                defered.resolve(alreadyNative);
            }
        } else {
            defered.resolve([]);
        }

        return defered.promise;
    }

    upload(file: File, groupGuid: string): angular.IPromise<ImportResultDto<Identifier>> {
        let retval: ImportResultDto<Identifier>;
        const url = this.controllerUrl() + 'upload/' + groupGuid;
        return this._upload
            .uploadFile<ImportResultDto<Identifier>>(url, file)
            .then(response => {
                const data: ImportResultDto<Identifier> = response;
                this._requestService.cleanCacheAtV1('identifier/');
                this._requestService.cleanCacheAtV1('viewModel/guidList');
                this._requestService.cleanCacheAtV1('viewModel/all');
                this._requestService.cleanCacheAtV1('group');
                retval = data;
                return this._groupService.getAll();
            })
            .then((dto: GroupDto[]) => {
                const groups = dto.map(d => d.group);
                retval.updated.forEach(t => {
                    this._requestService.cleanCache(`${this.controllerUrl()}${t.guid}`);
                    const g = _.find(groups, g => !!_.find(g.identifierCollection, i => i.guid === t.guid));
                    if (g) {
                        this._requestService.cleanCacheAtV1(`group/${g.guid}`);
                    }
                });
                return retval;
            });
    }
}
